import { Header, Cell, flexRender, RowData, Column } from '@tanstack/react-table';
import { INaviTableTooltip, NaviTableColumnDef } from './interfaces';
import { Directions } from '../TooltipV2/TooltipV2';
import { CSSProperties } from 'react';

const getColumnPinBorder = (
  isLastLeftPinnedColumn: boolean,
  isFirstRightPinnedColumn: boolean
): CSSProperties => {
  if (isLastLeftPinnedColumn) {
    return {
      borderRight: '1px solid var(--greyscale-light)'
    };
  }
  if (isFirstRightPinnedColumn) {
    return {
      borderLeft: '1px solid var(--greyscale-light)'
    };
  }
  return {};
};

export const getColumnStyle = <TData extends RowData, TValue = unknown>(
  column: Column<TData, TValue>,
  totalWidth: number,
  cellStyle?: CSSProperties,
  sizeColumnsToFit?: boolean
): CSSProperties => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right');
  // flex basis is a percentage of the total width
  const sizeStyles: CSSProperties = {};
  if (sizeColumnsToFit) {
    // if we want columns to fit in exactly, we don't have any horizonal scroll in this case
    sizeStyles.flexBasis = `${totalWidth ? (column.getSize() / totalWidth) * 100 : 100}%`;
  } else {
    sizeStyles.width = `${column.getSize()}px`;
  }
  const pinBorderStyle = getColumnPinBorder(isLastLeftPinnedColumn, isFirstRightPinnedColumn);
  const styles: React.CSSProperties = {
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? 0 : undefined,
    opacity: 1,
    position: isPinned ? 'sticky' : undefined,
    zIndex: isPinned ? 1 : 0,
    minWidth: 1,
    backgroundColor: isPinned ? 'inherit' : undefined,
    ...sizeStyles,
    ...pinBorderStyle,
    ...cellStyle
  };

  return styles;
};

export const getNaviHeaderCellClasses = (header: Header<any, unknown>) => {
  const isSortable = header.column.getCanSort();
  if (isSortable) {
    return 'flex items-center cursor-pointer gap-2 max-w-full';
  }
  return '';
};

export const getTooltipInfo = <TData extends RowData, TValue = unknown>(
  cell: Cell<TData, TValue>
) => {
  const columnDef = cell.column.columnDef as NaviTableColumnDef<TData, TValue>;
  const cellValue = flexRender(cell.column.columnDef.cell, cell.getContext());
  let tooltipInfo: INaviTableTooltip | null = null;

  if (typeof columnDef.tooltip === 'boolean') {
    tooltipInfo = columnDef.tooltip
      ? {
          content: cellValue as string,
          position: Directions.TOP
        }
      : null;
  }

  if (typeof columnDef.tooltip === 'function') {
    tooltipInfo = columnDef.tooltip(cell);
  }

  return tooltipInfo;
};

export const getHeaderTooltipInfo = <TData extends RowData, TValue = unknown>(
  header: Header<TData, TValue>
) => {
  const columnDef = header.column.columnDef as NaviTableColumnDef<TData, TValue>;
  let tooltipInfo: INaviTableTooltip | null = null;

  if (typeof columnDef.headerTooltip === 'boolean') {
    tooltipInfo = columnDef.headerTooltip
      ? {
          content: columnDef.header as string,
          position: Directions.TOP
        }
      : null;
  }

  if (typeof columnDef.headerTooltip === 'function') {
    tooltipInfo = columnDef.headerTooltip(header);
  }

  return tooltipInfo;
};
